import React, { useEffect, useState, useCallback } from 'react';
import { requestPermissionThenSubscribe, unsubscribeFromPush, subscribeToPush, syncSubscriptionWithServer } from './push-notification';

const checkIosPwa = () => {
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isStandalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
  return { isIOS, isStandalone };
};

export default function PushToggle() {
  const [supported, setSupported] = useState(false);
  const [granted, setGranted] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [hintText, setHintText] = useState('');
  const [hintOpen, setHintOpen] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      const hasSW = 'serviceWorker' in navigator;
      const hasPM = 'PushManager' in window;
      const secure = window.isSecureContext;
      setSupported(hasSW && hasPM && secure);

      const perm = Notification?.permission || 'default';
      setPermission(perm);
      setGranted(perm === 'granted');

      // Источником истины для статуса используем локальное хранилище,
      // т.к. оно отражает факт успешной синхронизации с сервером
      const endpoint = localStorage.getItem('push_endpoint') || '';
      setSubscribed(!!endpoint);
    } catch {
      setSubscribed(false);
    }
  }, []);

  useEffect(() => {
    refreshStatus();
    // Подписываемся на изменения разрешения в браузере, чтобы синхронизировать переключатель
    let permStatus;
    try {
      if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions
          .query({ name: 'notifications' })
          .then((status) => {
            permStatus = status;
            setPermission(status.state);
            setGranted(status.state === 'granted');
            status.onchange = () => {
              setPermission(status.state);
              setGranted(status.state === 'granted');
              refreshStatus();
              // Реакция на смену разрешений в браузере
              (async () => {
                if (!supported) return;
                if (status.state === 'denied' || status.state === 'prompt') {
                  // Пользователь заблокировал или сбросил разрешение — снимаем подписку и удаляем на сервере
                  try { await unsubscribeFromPush(); } catch {}
                } else if (status.state === 'granted') {
                  try {
                    const hadEndpoint = !!localStorage.getItem('push_endpoint');
                    if (hadEndpoint) {
                      await syncSubscriptionWithServer();
                    } else {
                      await subscribeToPush();
                    }
                  } catch {}
                }
              })();
            };
          })
          .catch(() => {});
      }
    } catch {}
    return () => {
      if (permStatus) permStatus.onchange = null;
    };
  }, [refreshStatus]);

  
  const getEnvInfo = () => {
    const ua = (navigator.userAgent || '').toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isAndroid = /android/.test(ua);
    const isMac = /macintosh|mac os x/.test(ua);
    const isWindows = /windows/.test(ua);
    const isStandalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
    const isEdge = ua.includes('edg');
    const isOpera = ua.includes('opr') || ua.includes('opera');
    const isYandex = ua.includes('yabrowser');
    const isFirefox = ua.includes('firefox');
    const isSafari = !isFirefox && !isEdge && !ua.includes('chrome') && ua.includes('safari');
    const isChrome = ua.includes('chrome') && !isEdge && !isOpera && !isYandex;
    return { isIOS, isAndroid, isMac, isWindows, isStandalone, isEdge, isOpera, isYandex, isFirefox, isSafari, isChrome };
  };

  const buildBlockedHint = () => {
    const env = getEnvInfo();
    if (env.isIOS) {
      if (!env.isStandalone) {
        return 'iOS: Установите сайт на Домой (поделиться → На экран «Домой»). После установки откройте приложение и включите уведомления.';
      }
      return 'iOS PWA: Откройте Настройки iOS → Уведомления → найдите приложение на Домой → Разрешить уведомления. Затем перезапустите приложение.';
    }
    if (env.isAndroid) {
      return 'Android: Нажмите на значок замка слева от адреса → Разрешения/Настройки сайта → Уведомления → Разрешить, затем обновите страницу.';
    }
    if (env.isFirefox) {
      return 'Firefox: Нажмите на значок замка слева от адреса → Права → Отправлять уведомления → Разрешить, затем обновите страницу.';
    }
    if (env.isEdge) {
      return 'Edge: Нажмите на замок → Разрешения сайта → Уведомления → Разрешить, затем обновите страницу.';
    }
    if (env.isOpera) {
      return 'Opera: Нажмите на замок → Настройки сайта → Уведомления → Разрешить, затем обновите страницу.';
    }
    if (env.isYandex) {
      return 'Яндекс.Браузер: Нажмите на замок → Разрешения → Уведомления → Разрешить, затем обновите страницу.';
    }
    if (env.isSafari && (env.isMac || !env.isAndroid)) {
      return 'Safari: Откройте Safari → Настройки веб‑сайтов → Уведомления → выберите этот сайт и установите «Разрешить», затем обновите страницу.';
    }
    return 'Нажмите на значок замка слева от адреса → Разрешения/Настройки сайта → Уведомления → Разрешить, затем обновите страницу.';
  };

  const showBlockedHint = () => {
    const text = buildBlockedHint();
    setHintText(text);
    setHintOpen(true);
    window.setTimeout(() => setHintOpen(false), 8000);
  };

  const onToggle = async () => {
    try {
      setLoading(true);
      if (permission === 'denied') {
        // Нельзя программно включить/выключить при блокировке — показываем понятную подсказку
        showBlockedHint();
        return;
      }

      if (subscribed && permission === 'granted') {
        const ok = await unsubscribeFromPush();
        if (ok) {
          setSubscribed(false); // мгновенно отражаем статус в UI
        }
      } else {
        const ok = await requestPermissionThenSubscribe();
        if (ok) {
          setSubscribed(true); // мгновенно отражаем статус в UI
        }
      }
    } finally {
      setLoading(false);
      refreshStatus();
    }
  };

  if (!supported) {
    const { isIOS, isStandalone } = checkIosPwa();
    const iosNote = isIOS && !isStandalone ? ' (iOS: доступно только после установки на Домой)' : '';
    return (
      <div className="bm-item menu-item" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
        Уведомления недоступны{iosNote}
      </div>
    );
  }

  const statusText =
    permission === 'denied'
      ? 'Заблокированы'
      : permission === 'granted' && subscribed
      ? 'Включены'
      : 'Выключены';

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className="bm-item menu-item"
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onToggle();
        }}
        style={{
          width: '100%',
          textAlign: 'left',
          position: 'absolute',
          bottom: 12,
          left: 0,
          right: 0,
          color: 'white',
          background: 'transparent',
          border: 'none',
          paddingLeft: 15,
          pointerEvents: loading ? 'none' : 'auto',
          opacity: loading ? 0.6 : 1,
        }}
      >
        {`Уведомления: ${statusText}`}
      </div>
      {hintOpen && (
        <div
          className="bm-item"
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            bottom: 48,
            background: 'rgba(0,0,0,0.85)',
            color: 'white',
            padding: '10px 12px',
            borderRadius: 8,
            fontSize: 12,
            lineHeight: '16px',
            zIndex: 5,
          }}
          onClick={() => setHintOpen(false)}
        >
          {hintText}
        </div>
      )}
    </>
  );
}



